<template>
	<div
		class="upgrades-view"
		:class="{ 'pointer-events-none': isCameraActive }"
	>
		<ModalWindow
			:heading="translate('ui.upgrades')"
			:hasActionBar="true"
			:hasStatusText="true"
			:statusData="upgradeStats"
			:isElevated="false"
			@info="openAboutPopup"
			@change-visibility="toggleVisibilityForCompleteUpgrades"
			@labelSelected="handleLabelSelected"
		>
			<template v-if="eventStore.isActive" #content>
				<Tabs :tabs="upgradesTabs" :selected-index="paramTabIndex">
					<template v-slot:tab1>
						<UpgradesTab
							:upgrades="getCurrentEventUpgrades(attributes.type)"
							:isEventType="true"
							:eventType="
								attributes.type ?? EventCategory.CHRISTMAS
							"
						/>
					</template>
					<template v-slot:tab2>
						<UpgradesTab :upgrades="upgrades" />
					</template>
				</Tabs>
			</template>
			<template v-else #content>
				<UpgradesTab :upgrades="upgrades" />
			</template>
		</ModalWindow>
	</div>
</template>

<script lang="ts" setup>
import ModalWindow from "@/components/ModalWindow.vue";
import Tabs from "@/components/Tabs.vue";
import AboutUpgradesPopup from "@/components/Upgrades/AboutUpgradesPopup.vue";
import UpgradesTab from "@/components/Upgrades/UpgradesTab.vue";
import { useEventStore } from "@/event";
import type { UpgradeId, UpgradeItem } from "@/game/entities/config/upgrades";
import { upgrades } from "@/game/entities/config/upgrades";
import { getCurrentEventUpgrades } from "@/game/entities/config/upgrades/event";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useRuntimeStore from "@/store/useRuntimeStore";
import useSettlementsStore from "@/store/useSettlementsStore";
import useUpgradesStore from "@/store/useUpgradesStore";
import useUserStore from "@/store/useUserStore";
import { EventCategory, type IconTypes } from "@/types/ui";
import type { FilterItems, UpgradeStat } from "@/types/upgrades";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, markRaw, onUnmounted } from "vue";
import { useRoute } from "vue-router";

const popupStore = usePopupStore();

const upgradesStore = useUpgradesStore();
const { getAllUpgrades } = storeToRefs(upgradesStore);

const runtimeStore = useRuntimeStore();
const { isCameraActive } = storeToRefs(runtimeStore);

const userStore = useUserStore();
const { setNewUpgradesIds } = userStore;

const { mainLevel } = storeToRefs(useSettlementsStore());

const route = useRoute();
const eventStore = useEventStore();

const { attributes } = storeToRefs(eventStore);

const openAboutPopup = (): void => {
	popupStore.showPopup({
		id: PopupDictionary.ABOUT_UPGRADES,
		type: PopupType.INFO,
		heading: translate("ui.about_upgrades"),
		height: 12,
		component: markRaw(AboutUpgradesPopup),
	});
};

const upgradesTabs = computed(
	(): { name: string; showEventIcon?: boolean }[] => {
		if (eventStore.isActive) {
			return [
				{ name: "event.event", showEventIcon: true },
				{ name: "ui.basic" },
			];
		}
		return [{ name: "ui.basic" }];
	},
);
const paramTabIndex = computed((): number => +route.params.tab || 0);

const upgradeStats = computed((): UpgradeStat => {
	let totalUpgrades = new Map<UpgradeId, UpgradeItem>([...upgrades]);

	if (eventStore.isActive && route.query.event) {
		totalUpgrades = new Map<UpgradeId, UpgradeItem>([
			...getCurrentEventUpgrades(attributes.value.type),
		]);
	}

	const ownedCount = Array.from(totalUpgrades.entries()).reduce(
		(acc: number, [upgradeId]: [UpgradeId, UpgradeItem]): number => {
			return acc + (getAllUpgrades.value[upgradeId] ?? 0);
		},
		0,
	);

	return {
		current: ownedCount,
		total:
			Array.from(totalUpgrades.entries())
				?.filter(
					(upgrade: [UpgradeId, UpgradeItem]): boolean =>
						upgrade[1].levelUnlock <= mainLevel.value,
				)
				.reduce(
					(acc: number, upgrade): number => acc + upgrade[1].maxLevel,
					0,
				) || 0,
	};
});

const toggleVisibilityForCompleteUpgrades = (
	visibilityType: IconTypes.HIDE | IconTypes.SHOW,
): void => {
	userStore.setUpgradeFilterVisibility(visibilityType);
};

const handleLabelSelected = (labelSelected: FilterItems): void => {
	runtimeStore.setUpgradeFilterValue(labelSelected);
};

onUnmounted((): void => {
	setNewUpgradesIds([]);
});
</script>

<style lang="scss" scoped>
.upgrades-view {
	.upgrades-container {
		@include flex-col-center;
		flex: 1 1 0;
	}

	.upgrades {
		@include flex-col-center;
		gap: 0.5rem;
		width: 100%;
	}

	.no-upgrades {
		flex: 1 1 auto;
		@include flex-col-center;

		.heading {
			color: #f2dcb5;
			font-weight: 800;
			font-size: 1rem;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			text-transform: uppercase;
		}
	}

	.level-up {
		margin: 0.563rem;
		color: #fff3dd;
		font-size: 0.75rem;
		line-height: 0.75rem;
		text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
	}
}
</style>
